import React, { useCallback, useEffect, useMemo, useState } from "react";
import advancedFollowUpReportService, {
  AdvancedFollowUpBooking,
  AdvancedFollowUpFilters,
  AdvancedKPIs,
  ALL_COLUMNS,
  ColumnConfig,
  FilterCondition,
  Project,
  ReportPreset,
} from "../services/advancedFollowUpReportService";
import "../css/AdvancedFollowUpReport.css";

// ─── Helper Constants ────────────────────────────────────────────────────

const FILTER_FIELDS = [
  { value: "pendingPercent", label: "Pending %", type: "number" },
  { value: "receivedPercent", label: "Received %", type: "number" },
  { value: "totalPending", label: "Total Pending", type: "number" },
  { value: "totalReceived", label: "Total Received", type: "number" },
  { value: "totalAmount", label: "Total Amount", type: "number" },
  { value: "saleDeedAmount", label: "Sale Deed Amount", type: "number" },
  { value: "saleDeedReceived", label: "Sale Deed Received", type: "number" },
  { value: "receivableAmount", label: "Receivable Amount", type: "number" },
  { value: "receivableReceived", label: "Receivable Received", type: "number" },
  { value: "receivedInPeriod", label: "Received in Period", type: "number" },
  { value: "daysSinceBooking", label: "Days Since Booking", type: "number" },
  { value: "daysSinceLastPayment", label: "Days Since Last Payment", type: "number" },
  { value: "paymentCount", label: "Payment Count", type: "number" },
  { value: "paymentTerms", label: "Payment Terms", type: "text" },
  { value: "unitType", label: "Unit Type", type: "text" },
  { value: "wing", label: "Wing", type: "text" },
  { value: "status", label: "Status", type: "text" },
  { value: "customerName", label: "Customer Name", type: "text" },
  { value: "projectName", label: "Project Name", type: "text" },
];

const NUMERIC_OPERATORS = [
  { value: ">", label: ">" },
  { value: ">=", label: ">=" },
  { value: "<", label: "<" },
  { value: "<=", label: "<=" },
  { value: "=", label: "=" },
  { value: "between", label: "Between" },
];

const TEXT_OPERATORS = [
  { value: "=", label: "Equals" },
  { value: "!=", label: "Not Equals" },
];

// ─── Quick Date Ranges ───────────────────────────────────────────────────

function getQuickDateRange(preset: string): { from: string; to: string } {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  switch (preset) {
    case "thisMonth":
      return {
        from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case "lastMonth":
      return {
        from: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: fmt(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case "last3Months":
      return {
        from: fmt(new Date(now.getFullYear(), now.getMonth() - 3, 1)),
        to: fmt(now),
      };
    case "last6Months":
      return {
        from: fmt(new Date(now.getFullYear(), now.getMonth() - 6, 1)),
        to: fmt(now),
      };
    case "thisYear":
      return {
        from: fmt(new Date(now.getFullYear(), 0, 1)),
        to: fmt(now),
      };
    case "lastYear":
      return {
        from: fmt(new Date(now.getFullYear() - 1, 0, 1)),
        to: fmt(new Date(now.getFullYear() - 1, 11, 31)),
      };
    case "thisQuarter": {
      const q = Math.floor(now.getMonth() / 3);
      return {
        from: fmt(new Date(now.getFullYear(), q * 3, 1)),
        to: fmt(now),
      };
    }
    default:
      return { from: "", to: "" };
  }
}

// ─── Component ───────────────────────────────────────────────────────────

const AdvancedFollowUpReport: React.FC = () => {
  // ── Filter State ────────────────────────────────────────────────
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [bookingDateFrom, setBookingDateFrom] = useState("");
  const [bookingDateTo, setBookingDateTo] = useState("");
  const [paymentDateFrom, setPaymentDateFrom] = useState("");
  const [paymentDateTo, setPaymentDateTo] = useState("");
  const [filterLogic, setFilterLogic] = useState<"AND" | "OR">("AND");
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);

  // ── Data State ──────────────────────────────────────────────────
  const [projects, setProjects] = useState<Project[]>([]);
  const [kpis, setKpis] = useState<AdvancedKPIs>({
    totalBookings: 0,
    totalSaleDeed: 0,
    totalReceivable: 0,
    totalExpected: 0,
    totalReceived: 0,
    totalPending: 0,
    averageReceivedPercent: 0,
    averagePendingPercent: 0,
    highRiskCount: 0,
    receivedInPeriod: 0,
  });
  const [bookings, setBookings] = useState<AdvancedFollowUpBooking[]>([]);

  // ── UI State ────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState("");

  // ── Column & Preset State ──────────────────────────────────────
  const [columns, setColumns] = useState<ColumnConfig[]>(
    advancedFollowUpReportService.getColumnConfig()
  );
  const [presets, setPresets] = useState<ReportPreset[]>(
    advancedFollowUpReportService.getPresets()
  );

  // ── Sort State ─────────────────────────────────────────────────
  const [sortField, setSortField] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // ── Visible & Sorted Columns ───────────────────────────────────
  const visibleColumns = useMemo(
    () => columns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [columns]
  );

  // ── Sorted Bookings ────────────────────────────────────────────
  const sortedBookings = useMemo(() => {
    if (!sortField) return bookings;
    const sorted = [...bookings].sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return sorted;
  }, [bookings, sortField, sortDir]);

  // ── Load Data ──────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: AdvancedFollowUpFilters | undefined =
        filterConditions.length > 0
          ? { logic: filterLogic, conditions: filterConditions }
          : undefined;

      const data = await advancedFollowUpReportService.getReportData(
        selectedProject !== "all" ? selectedProject : undefined,
        bookingDateFrom || undefined,
        bookingDateTo || undefined,
        paymentDateFrom || undefined,
        paymentDateTo || undefined,
        filters
      );

      setProjects(data.projects);
      setKpis(data.kpis);
      setBookings(data.bookings);
    } catch (err: any) {
      console.error("Error loading advanced follow-up report:", err);
      setError(err.response?.data?.error || "Failed to load report data");
    } finally {
      setLoading(false);
    }
  }, [selectedProject, bookingDateFrom, bookingDateTo, paymentDateFrom, paymentDateTo, filterConditions, filterLogic]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Quick Date Buttons ─────────────────────────────────────────
  const handleQuickDate = (preset: string) => {
    const { from, to } = getQuickDateRange(preset);
    setPaymentDateFrom(from);
    setPaymentDateTo(to);
  };

  // ── Filter Condition Handlers ──────────────────────────────────
  const addCondition = () => {
    setFilterConditions([
      ...filterConditions,
      { field: "pendingPercent", operator: ">", value: 0 },
    ]);
  };

  const removeCondition = (index: number) => {
    setFilterConditions(filterConditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, key: keyof FilterCondition, val: any) => {
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], [key]: val };

    if (key === "field") {
      const fieldDef = FILTER_FIELDS.find((f) => f.value === val);
      const isText = fieldDef?.type === "text";
      updated[index].operator = isText ? "=" : ">";
      updated[index].value = isText ? "" : 0;
    }

    if (key === "operator") {
      const fieldDef = FILTER_FIELDS.find((f) => f.value === updated[index].field);
      updated[index].value = val === "between" ? [0, 0] : fieldDef?.type === "text" ? "" : 0;
    }

    setFilterConditions(updated);
  };

  const updateBetween = (index: number, pos: 0 | 1, val: number) => {
    const updated = [...filterConditions];
    const cur = updated[index].value as [number, number];
    const next: [number, number] = [...cur] as [number, number];
    next[pos] = val;
    updated[index].value = next;
    setFilterConditions(updated);
  };

  // ── Column Handlers ────────────────────────────────────────────
  const toggleColumn = (key: string) => {
    const updated = columns.map((c) =>
      c.key === key ? { ...c, visible: !c.visible } : c
    );
    setColumns(updated);
    advancedFollowUpReportService.saveColumnConfig(updated);
  };

  const moveColumn = (key: string, direction: "up" | "down") => {
    const idx = columns.findIndex((c) => c.key === key);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= columns.length) return;

    const updated = [...columns];
    const tempOrder = updated[idx].order;
    updated[idx] = { ...updated[idx], order: updated[swapIdx].order };
    updated[swapIdx] = { ...updated[swapIdx], order: tempOrder };
    updated.sort((a, b) => a.order - b.order);
    setColumns(updated);
    advancedFollowUpReportService.saveColumnConfig(updated);
  };

  const resetColumns = () => {
    setColumns([...ALL_COLUMNS]);
    advancedFollowUpReportService.saveColumnConfig([...ALL_COLUMNS]);
  };

  // ── Sort Handler ───────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (field === "srNo") return;
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // ── Preset Handlers ────────────────────────────────────────────
  const savePreset = () => {
    if (!presetName.trim()) return;
    const preset: ReportPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      createdAt: new Date().toISOString(),
      filters: {
        project: selectedProject,
        bookingDateFrom,
        bookingDateTo,
        paymentDateFrom,
        paymentDateTo,
        conditions: filterConditions,
        logic: filterLogic,
      },
      columns: columns,
    };
    advancedFollowUpReportService.savePreset(preset);
    setPresets(advancedFollowUpReportService.getPresets());
    setPresetName("");
    setShowPresetModal(false);
  };

  const loadPreset = (preset: ReportPreset) => {
    setSelectedProject(preset.filters.project);
    setBookingDateFrom(preset.filters.bookingDateFrom);
    setBookingDateTo(preset.filters.bookingDateTo);
    setPaymentDateFrom(preset.filters.paymentDateFrom);
    setPaymentDateTo(preset.filters.paymentDateTo);
    setFilterConditions(preset.filters.conditions);
    setFilterLogic(preset.filters.logic);
    setColumns(preset.columns);
    advancedFollowUpReportService.saveColumnConfig(preset.columns);
  };

  const deletePreset = (id: string) => {
    advancedFollowUpReportService.deletePreset(id);
    setPresets(advancedFollowUpReportService.getPresets());
  };

  // ── Clear All ──────────────────────────────────────────────────
  const clearAll = () => {
    setSelectedProject("all");
    setBookingDateFrom("");
    setBookingDateTo("");
    setPaymentDateFrom("");
    setPaymentDateTo("");
    setFilterConditions([]);
    setFilterLogic("AND");
    setSortField("");
    setSortDir("asc");
    setTimeout(() => loadData(), 50);
  };

  // ── Export CSV ─────────────────────────────────────────────────
  const handleExport = () => {
    if (sortedBookings.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = visibleColumns.map((c) => c.label);
    const rows = sortedBookings.map((b, index) =>
      visibleColumns.map((col) => formatCellValue(col, b, index))
    );

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `advanced-follow-up-report-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Formatting ─────────────────────────────────────────────────
  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const formatCellValue = (
    col: ColumnConfig,
    booking: AdvancedFollowUpBooking,
    index: number
  ): string => {
    if (col.key === "srNo") return String(index + 1);
    const val = (booking as any)[col.key];
    if (val === null || val === undefined) return "N/A";
    switch (col.type) {
      case "currency":
        return formatCurrency(val);
      case "percent":
        return val.toFixed(1) + "%";
      case "number":
        return String(val);
      case "date":
        return val || "N/A";
      default:
        return String(val);
    }
  };

  const formatCellRaw = (col: ColumnConfig, booking: AdvancedFollowUpBooking, index: number): string => {
    if (col.key === "srNo") return String(index + 1);
    const val = (booking as any)[col.key];
    if (val === null || val === undefined) return "N/A";
    return String(val);
  };

  // ── Row Risk Class ─────────────────────────────────────────────
  const getRowClass = (b: AdvancedFollowUpBooking): string => {
    if (b.pendingPercent > 70 || (b.daysSinceLastPayment !== null && b.daysSinceLastPayment > 90)) {
      return "afr-row-high-risk";
    }
    if (b.pendingPercent > 40) {
      return "afr-row-medium-risk";
    }
    return "";
  };

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="afr-container">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="afr-header">
        <div className="afr-header-left">
          <h1>Advanced Follow-Up Report</h1>
          <p className="afr-subtitle">
            Comprehensive payment collection tracking with Sale Deed &amp; Receivable breakdown
          </p>
        </div>
        <div className="afr-header-actions">
          <button onClick={handleExport} className="afr-btn afr-btn-export">
            Export CSV
          </button>
          <button onClick={() => window.print()} className="afr-btn afr-btn-print">
            Print
          </button>
        </div>
      </div>

      {/* ─── Preset Bar ─────────────────────────────────────────── */}
      <div className="afr-preset-bar">
        <div className="afr-preset-left">
          <label>Saved Views:</label>
          <select
            className="afr-preset-select"
            value=""
            onChange={(e) => {
              const preset = presets.find((p) => p.id === e.target.value);
              if (preset) loadPreset(preset);
            }}
          >
            <option value="">-- Select a saved view --</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {presets.length > 0 && (
            <select
              className="afr-preset-delete-select"
              value=""
              onChange={(e) => {
                if (e.target.value && window.confirm("Delete this saved view?")) {
                  deletePreset(e.target.value);
                }
              }}
            >
              <option value="">Delete a view...</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="afr-preset-right">
          <button
            onClick={() => setShowPresetModal(true)}
            className="afr-btn afr-btn-save"
          >
            Save Current View
          </button>
          <button
            onClick={() => setShowColumnManager(!showColumnManager)}
            className="afr-btn afr-btn-columns"
          >
            {showColumnManager ? "Hide" : "Customize"} Columns
          </button>
        </div>
      </div>

      {/* ─── Column Manager ─────────────────────────────────────── */}
      {showColumnManager && (
        <div className="afr-column-manager">
          <div className="afr-column-manager-header">
            <h3>Column Configuration</h3>
            <button onClick={resetColumns} className="afr-btn afr-btn-sm">
              Reset to Default
            </button>
          </div>
          <div className="afr-column-list">
            {columns
              .sort((a, b) => a.order - b.order)
              .map((col) => (
                <div key={col.key} className="afr-column-item">
                  <label className="afr-column-toggle">
                    <input
                      type="checkbox"
                      checked={col.visible}
                      onChange={() => toggleColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                  <div className="afr-column-arrows">
                    <button
                      onClick={() => moveColumn(col.key, "up")}
                      className="afr-arrow-btn"
                      title="Move up"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveColumn(col.key, "down")}
                      className="afr-arrow-btn"
                      title="Move down"
                    >
                      ▼
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ─── Filter Bar ─────────────────────────────────────────── */}
      <div
        className="afr-filter-bar"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            loadData();
          }
        }}
      >
        <div className="afr-filter-row">
          <div className="afr-filter-group">
            <label>Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="all">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id.toString()}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="afr-filter-group">
            <label>Booking From</label>
            <input
              type="date"
              value={bookingDateFrom}
              onChange={(e) => setBookingDateFrom(e.target.value)}
            />
          </div>
          <div className="afr-filter-group">
            <label>Booking To</label>
            <input
              type="date"
              value={bookingDateTo}
              onChange={(e) => setBookingDateTo(e.target.value)}
            />
          </div>

          <div className="afr-filter-separator" />

          <div className="afr-filter-group">
            <label>Payment From</label>
            <input
              type="date"
              value={paymentDateFrom}
              onChange={(e) => setPaymentDateFrom(e.target.value)}
            />
          </div>
          <div className="afr-filter-group">
            <label>Payment To</label>
            <input
              type="date"
              value={paymentDateTo}
              onChange={(e) => setPaymentDateTo(e.target.value)}
            />
          </div>
        </div>

        {/* Quick Date Buttons */}
        <div className="afr-quick-dates">
          <span className="afr-quick-label">Quick:</span>
          {[
            { key: "thisMonth", label: "This Month" },
            { key: "lastMonth", label: "Last Month" },
            { key: "last3Months", label: "Last 3 Months" },
            { key: "last6Months", label: "Last 6 Months" },
            { key: "thisQuarter", label: "This Quarter" },
            { key: "thisYear", label: "This Year" },
            { key: "lastYear", label: "Last Year" },
          ].map((q) => (
            <button
              key={q.key}
              onClick={() => handleQuickDate(q.key)}
              className="afr-quick-btn"
            >
              {q.label}
            </button>
          ))}
        </div>

        <div className="afr-filter-actions">
          <button onClick={loadData} className="afr-btn afr-btn-primary">
            Apply Filters
          </button>
          <button onClick={clearAll} className="afr-btn afr-btn-secondary">
            Clear All
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="afr-btn afr-btn-advanced"
          >
            {showFilters ? "Hide" : "Show"} Advanced Filters
          </button>
        </div>
      </div>

      {/* ─── Advanced Filter Builder ────────────────────────────── */}
      {showFilters && (
        <div className="afr-filter-builder">
          <div className="afr-fb-header">
            <h3>Advanced Filter Builder</h3>
            <div className="afr-fb-logic">
              <label>Match:</label>
              <select
                value={filterLogic}
                onChange={(e) => setFilterLogic(e.target.value as "AND" | "OR")}
              >
                <option value="AND">ALL conditions (AND)</option>
                <option value="OR">ANY condition (OR)</option>
              </select>
            </div>
          </div>

          <div className="afr-conditions">
            {filterConditions.map((cond, idx) => {
              const fieldDef = FILTER_FIELDS.find((f) => f.value === cond.field);
              const isText = fieldDef?.type === "text";
              const operators = isText ? TEXT_OPERATORS : NUMERIC_OPERATORS;

              return (
                <div key={idx} className="afr-condition-row">
                  <span className="afr-cond-num">{idx + 1}.</span>

                  <select
                    value={cond.field}
                    onChange={(e) => updateCondition(idx, "field", e.target.value)}
                    className="afr-cond-field"
                  >
                    {FILTER_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={cond.operator}
                    onChange={(e) => updateCondition(idx, "operator", e.target.value)}
                    className="afr-cond-op"
                  >
                    {operators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  {cond.operator === "between" ? (
                    <div className="afr-between">
                      <input
                        type="number"
                        value={(cond.value as [number, number])[0]}
                        onChange={(e) => updateBetween(idx, 0, parseFloat(e.target.value) || 0)}
                        placeholder="Min"
                      />
                      <span>to</span>
                      <input
                        type="number"
                        value={(cond.value as [number, number])[1]}
                        onChange={(e) => updateBetween(idx, 1, parseFloat(e.target.value) || 0)}
                        placeholder="Max"
                      />
                    </div>
                  ) : isText ? (
                    <input
                      type="text"
                      value={cond.value as string}
                      onChange={(e) => updateCondition(idx, "value", e.target.value)}
                      placeholder="Enter value"
                      className="afr-cond-value"
                    />
                  ) : (
                    <input
                      type="number"
                      value={cond.value as number}
                      onChange={(e) =>
                        updateCondition(idx, "value", parseFloat(e.target.value) || 0)
                      }
                      placeholder="Enter value"
                      className="afr-cond-value"
                    />
                  )}

                  <button
                    onClick={() => removeCondition(idx)}
                    className="afr-btn-remove"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <button onClick={addCondition} className="afr-btn afr-btn-add">
            + Add Condition
          </button>
        </div>
      )}

      {/* ─── KPI Cards ──────────────────────────────────────────── */}
      <div className="afr-kpis">
        <div className="afr-kpi-card">
          <div className="afr-kpi-value">{kpis.totalBookings}</div>
          <div className="afr-kpi-label">Total Bookings</div>
        </div>
        <div className="afr-kpi-card afr-kpi-success">
          <div className="afr-kpi-value">{formatCurrency(kpis.totalReceived)}</div>
          <div className="afr-kpi-label">Total Received</div>
        </div>
        <div className="afr-kpi-card afr-kpi-warning">
          <div className="afr-kpi-value">{formatCurrency(kpis.totalPending)}</div>
          <div className="afr-kpi-label">Total Pending</div>
        </div>
        <div className="afr-kpi-card afr-kpi-info">
          <div className="afr-kpi-value">{kpis.averageReceivedPercent.toFixed(1)}%</div>
          <div className="afr-kpi-label">Avg Received %</div>
        </div>
        <div className="afr-kpi-card afr-kpi-danger">
          <div className="afr-kpi-value">{kpis.highRiskCount}</div>
          <div className="afr-kpi-label">High Risk</div>
        </div>
        {(paymentDateFrom || paymentDateTo) && (
          <div className="afr-kpi-card afr-kpi-period">
            <div className="afr-kpi-value">{formatCurrency(kpis.receivedInPeriod)}</div>
            <div className="afr-kpi-label">Received in Period</div>
          </div>
        )}
      </div>

      {/* ─── Loading / Error ────────────────────────────────────── */}
      {loading && (
        <div className="afr-loading">
          <div className="afr-spinner" />
          <span>Loading report data...</span>
        </div>
      )}
      {error && <div className="afr-error">{error}</div>}

      {/* ─── Data Table ─────────────────────────────────────────── */}
      {!loading && !error && (
        <div className="afr-table-container">
          <div className="afr-table-info">
            Showing {sortedBookings.length} booking{sortedBookings.length !== 1 ? "s" : ""}
            {visibleColumns.length < columns.length && (
              <span className="afr-col-count">
                {" "}— {visibleColumns.length} of {columns.length} columns visible
              </span>
            )}
          </div>
          <div className="afr-table-scroll">
            <table className="afr-table">
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={`afr-th ${col.type === "currency" || col.type === "percent" || col.type === "number" ? "afr-th-right" : ""} ${sortField === col.key ? "afr-th-sorted" : ""}`}
                    >
                      <span>{col.label}</span>
                      {sortField === col.key && (
                        <span className="afr-sort-icon">
                          {sortDir === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedBookings.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="afr-no-data">
                      No bookings found with current filters
                    </td>
                  </tr>
                ) : (
                  sortedBookings.map((booking, index) => (
                    <tr key={booking.bookingId} className={getRowClass(booking)}>
                      {visibleColumns.map((col) => (
                        <td
                          key={col.key}
                          className={`${col.type === "currency" || col.type === "percent" || col.type === "number" ? "afr-td-right" : ""} ${col.key === "pendingPercent" && booking.pendingPercent > 70 ? "afr-td-danger" : ""} ${col.key === "receivedPercent" && booking.receivedPercent > 80 ? "afr-td-success" : ""} ${col.key === "receivedInPeriod" && booking.receivedInPeriod > 0 ? "afr-td-highlight" : ""}`}
                        >
                          {formatCellValue(col, booking, index)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Save Preset Modal ──────────────────────────────────── */}
      {showPresetModal && (
        <div className="afr-modal-overlay" onClick={() => setShowPresetModal(false)}>
          <div className="afr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="afr-modal-header">
              <h3>Save Current View</h3>
              <button onClick={() => setShowPresetModal(false)} className="afr-modal-close">
                ✕
              </button>
            </div>
            <div className="afr-modal-body">
              <p>
                This will save your current filters, column selection, and column order
                so you can quickly load them later.
              </p>
              <div className="afr-modal-input-group">
                <label>View Name:</label>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="e.g. High Risk Bookings, Monthly Review"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") savePreset();
                  }}
                />
              </div>
              <div className="afr-modal-summary">
                <strong>Will save:</strong>
                <ul>
                  <li>Project: {selectedProject === "all" ? "All" : projects.find((p) => p.id.toString() === selectedProject)?.name || selectedProject}</li>
                  {bookingDateFrom && <li>Booking dates: {bookingDateFrom} to {bookingDateTo || "now"}</li>}
                  {paymentDateFrom && <li>Payment dates: {paymentDateFrom} to {paymentDateTo || "now"}</li>}
                  <li>{filterConditions.length} advanced condition{filterConditions.length !== 1 ? "s" : ""}</li>
                  <li>{visibleColumns.length} visible columns</li>
                </ul>
              </div>
            </div>
            <div className="afr-modal-footer">
              <button onClick={() => setShowPresetModal(false)} className="afr-btn afr-btn-secondary">
                Cancel
              </button>
              <button
                onClick={savePreset}
                className="afr-btn afr-btn-primary"
                disabled={!presetName.trim()}
              >
                Save View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFollowUpReport;
